import { isAxiosError } from 'axios'

import type { ServiceQueryOptionType } from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  CityRecap,
  DistrictRecap,
  NationalRecap,
  ProvinceRecap,
  RecapExtInstitutionResponse
} from './types'

const RECAP_REQUEST_TIMEOUT_MS = 30_000
const RECAP_QUERY_RETRY = 3

const RECAP_EXT_INSTITUTION_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['recap_ext_institution', key] as const

const recapGet = <T>(url: string) =>
  api.get<T>(url, { timeout: RECAP_REQUEST_TIMEOUT_MS })

const withRecapQueryDefaults = <T>(
  options: ServiceQueryOptionType<T>
): ServiceQueryOptionType<T> => ({
  retry: RECAP_QUERY_RETRY,
  ...options
})

const getNationalRecapExtensionInstitution = (): ServiceQueryOptionType<
  RecapExtInstitutionResponse<NationalRecap>
> =>
  withRecapQueryDefaults({
    queryKey: RECAP_EXT_INSTITUTION_QUERY_KEY({ type: 'national' }),
    queryFn: async () => {
      try {
        const response = await recapGet<
          RecapExtInstitutionResponse<NationalRecap>
        >('/report/kelembagaan/penyuluh/rekap')

        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return { data: { detail: [] }, message: 'No data found' }
          }
        }

        throw handleAxiosError(error)
      }
    }
  })

const getProvinceRecapExtensionInstitution = (
  provinceCode?: string
): ServiceQueryOptionType<RecapExtInstitutionResponse<ProvinceRecap>> =>
  withRecapQueryDefaults({
    queryKey: RECAP_EXT_INSTITUTION_QUERY_KEY({
      type: 'province',
      provinceId: provinceCode
    }),
    queryFn: async () => {
      try {
        const response = await recapGet<
          RecapExtInstitutionResponse<ProvinceRecap>
        >(`/report/kelembagaan/penyuluh/rekap/province/${provinceCode}`)

        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return { data: { detail: [] }, message: 'No data found' }
          }
        }
        throw handleAxiosError(error)
      }
    }
  })

const getCityRecapExtensionInstitution = ({
  provinceCode,
  cityCode
}: {
  provinceCode?: string
  cityCode?: string
}): ServiceQueryOptionType<RecapExtInstitutionResponse<CityRecap>> =>
  withRecapQueryDefaults({
    queryKey: RECAP_EXT_INSTITUTION_QUERY_KEY({
      type: 'city',
      provinceId: provinceCode,
      cityId: cityCode
    }),
    queryFn: async () => {
      try {
        const response = await recapGet<RecapExtInstitutionResponse<CityRecap>>(
          `/report/kelembagaan/penyuluh/rekap/province/${provinceCode}/city/${cityCode}`
        )

        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return { data: { detail: [] }, message: 'No data found' }
          }
        }
        throw handleAxiosError(error)
      }
    }
  })

const getDistrictRecapExtensionInstitution = ({
  provinceCode,
  cityCode,
  districtCode
}: {
  provinceCode?: string
  cityCode?: string
  districtCode?: string
}): ServiceQueryOptionType<RecapExtInstitutionResponse<DistrictRecap>> =>
  withRecapQueryDefaults({
    queryKey: RECAP_EXT_INSTITUTION_QUERY_KEY({
      type: 'district',
      provinceId: provinceCode,
      cityId: cityCode,
      districtId: districtCode
    }),
    queryFn: async () => {
      try {
        const response = await recapGet<
          RecapExtInstitutionResponse<DistrictRecap>
        >(
          `/report/kelembagaan/penyuluh/rekap/province/${provinceCode}/city/${cityCode}/district/${districtCode}`
        )

        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return { data: { detail: [] }, message: 'No data found' }
          }
        }
        throw handleAxiosError(error)
      }
    }
  })

export const recapExtInstitution = {
  queryKey: RECAP_EXT_INSTITUTION_QUERY_KEY('recap_ext_institution'),
  recap: {
    getNational: getNationalRecapExtensionInstitution,
    getProvince: getProvinceRecapExtensionInstitution,
    getCity: getCityRecapExtensionInstitution,
    getDistrict: getDistrictRecapExtensionInstitution
  }
}
